import React, { useEffect, useState } from 'react';
import { SidebarGroup } from './types';
import './Sidebar.css';
import { newProgressService } from '../../services/newProgress';

interface Props { 
  sidebar: SidebarGroup[]; 
  moduleTitle?: string; 
  activeHref?: string; 
}

const Sidebar: React.FC<Props> = ({ sidebar, moduleTitle, activeHref }) => {
  const [viewedLessons, setViewedLessons] = useState<Record<string, boolean>>({});
  const [lastUpdate, setLastUpdate] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(Date.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const [open, setOpen] = React.useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {};
    const findGroupWithActive = () => sidebar.findIndex(g => 
      g.items.some(i => (i.href || '').split('?')[0] === (activeHref || ''))
    );
    const activeGroupIndex = findGroupWithActive();
    sidebar.forEach((_, idx) => { 
      initial[idx] = activeGroupIndex >= 0 ? (idx === activeGroupIndex) : idx === 0; 
    });
    return initial;
  });

  const timerRef = React.useRef<number | null>(null);

  const closeAll = () => {
    setOpen(() => {
      const next: Record<number, boolean> = {};
      sidebar.forEach((_, i) => { next[i] = false; });
      return next;
    });
  };

  const toggle = (idx: number) => {
    const isOpen = !!open[idx];
    
    // Primeiro fecha todos os outros módulos
    closeAll();
    
    // Se o módulo clicado estava fechado, abre ele após um pequeno delay
    // para dar tempo da animação de fechamento dos outros terminar
    if (!isOpen) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      timerRef.current = window.setTimeout(() => {
        setOpen(prev => ({
          ...prev,
          [idx]: true
        }));
        timerRef.current = null;
      }, 100);
    }
  };

  useEffect(() => {
    const loadProgress = async () => {
      try {
        const lessonIds = sidebar.flatMap(group =>
          group.items
            .map(item => item.href?.split('/').pop()?.split('?')[0])
            .filter((id): id is string => !!id)
        );
        const progress = await newProgressService.getBatchProgress(lessonIds);
        setViewedLessons(progress);
      } catch (error) {
        console.error('Erro ao carregar progresso:', error);
      }
    };

    loadProgress();
  }, [sidebar, lastUpdate]);

  const title = moduleTitle ? (
    <div className="module-title" style={{fontWeight:600, opacity:.9, padding:'8px 0 6px'}}>
      {moduleTitle}
    </div>
  ) : null;

  return (
    <div>
      {title}
      <div className="sections" id="sections">
        {sidebar.map((group, gi) => (
          <div key={gi} className={`section-group ${open[gi] ? 'open' : ''}`}>
            <div 
              className={`section-item section-group-titulo ${open[gi] ? '' : 'collapsed'}`}
              aria-expanded={open[gi]} 
              onClick={() => toggle(gi)} 
              style={{ cursor: 'pointer' }}
            >
              <div className="item-bull">
                <span className="checkmark">✓</span>
              </div>
              <div className="item-wrap">
                <div className="item-title">{group.title}</div>
                {group.countLabel && <div className="item-label">{group.countLabel}</div>}
              </div>
            </div>
            <div className="section-group-items">
              {group.items.map((item, ii) => {
                const id = item.href?.split('/').pop()?.split('?')[0];
                const isWatched = id ? viewedLessons[id] : false;
                const isActive = (item.href || '').split('?')[0] === (activeHref || '');

                return (
                  <a
                    key={ii}
                    href={item.href}
                    className={`section-item progresso-click ${isWatched ? 'done' : ''} ${isActive ? 'active' : ''}`}
                    data-item-id={id}
                  >
                    <div className="item-bull">
                      {isWatched ? (
                        <span className="checkmark">✓</span>
                      ) : (
                        <span className="bull"></span>
                      )}
                    </div>
                    <div className="item-wrap">
                      <div className="item-title">{item.label}</div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;

